// src/store/slices/commonSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import commonApi from "api/commonApi";

// Existing Thunks
export const fetchEmployeeDropdown = createAsyncThunk(
  "common/fetchEmployeeDropdown",
  async ({ exclude_self = "" }, { rejectWithValue }) => {
    try {
      const data = await commonApi.getEmployeeDropdown(exclude_self);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load employees"
      );
    }
  }
);

export const fetchRequestDropdown = createAsyncThunk(
  "common/fetchRequestDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const data = await commonApi.getRequestDropdown();
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load requests"
      );
    }
  }
);

export const fetchCanteenDropdown = createAsyncThunk(
  "common/fetchCanteenDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const data = await commonApi.getCanteenDropdown();
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load canteen items"
      );
    }
  }
);

// NEW THUNKS: Issuance Employees
export const fetchIssuanceEmployeesIT = createAsyncThunk(
  "common/fetchIssuanceEmployeesIT",
  async (_, { rejectWithValue }) => {
    try {
      const data = await commonApi.getIssuanceEmployeesIT();
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load IT issuance employees"
      );
    }
  }
);

export const fetchIssuanceEmployeesNonIT = createAsyncThunk(
  "common/fetchIssuanceEmployeesNonIT",
  async (_, { rejectWithValue }) => {
    try {
      const data = await commonApi.getIssuanceEmployeesNonIT();
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load Non-IT issuance employees"
      );
    }
  }
);

// NEW THUNKS: Ticket Employees
export const fetchTicketEmployeesIT = createAsyncThunk(
  "common/fetchTicketEmployeesIT",
  async (_, { rejectWithValue }) => {
    try {
      const data = await commonApi.getTicketEmployeesIT();
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load IT ticket employees"
      );
    }
  }
);

export const fetchTicketEmployeesNonIT = createAsyncThunk(
  "common/fetchTicketEmployeesNonIT",
  async (_, { rejectWithValue }) => {
    try {
      const data = await commonApi.getTicketEmployeesNonIT();
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load Non-IT ticket employees"
      );
    }
  }
);

// NEW THUNKS: Stock (Updated to handle Category ID)
export const fetchITStock = createAsyncThunk(
  "common/fetchITStock",
  async (categoryId, { rejectWithValue }) => {
    try {
      const data = await commonApi.getITStock(categoryId);
      return { categoryId, data }; // Return structure for reducer
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load IT stock"
      );
    }
  }
);

export const fetchNonITStock = createAsyncThunk(
  "common/fetchNonITStock",
  async (categoryId, { rejectWithValue }) => {
    try {
      const data = await commonApi.getNonITStock(categoryId);
      return { categoryId, data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load Non-IT stock"
      );
    }
  }
);

export const fetchInventoryCategories = createAsyncThunk(
  "common/fetchInventoryCategories",
  async (_, { rejectWithValue }) => {
    try {
      const data = await commonApi.getInventoryCategories();
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load inventory categories"
      );
    }
  }
);

export const fetchInventorySubcategories = createAsyncThunk(
  "common/fetchInventorySubcategories",
  async (categoryId, { rejectWithValue }) => {
    try {
      const data = await commonApi.getInventorySubcategories(categoryId);
      return { categoryId, data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load subcategories"
      );
    }
  }
);

// NEW THUNK: Inventory Dropdown
export const fetchInventoryDropdown = createAsyncThunk(
  "common/fetchInventoryDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const data = await commonApi.getInventoryDropdown();
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load inventory dropdown"
      );
    }
  }
);

// Fetch available inventories for a request
export const fetchAvailableInventories = createAsyncThunk(
  "common/fetchAvailableInventories",
  async (requestId, { rejectWithValue }) => {
    try {
      const data = await commonApi.getAvailableInventories(requestId);
      return { requestId, data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load available inventories"
      );
    }
  }
);

// Generic Restore Thunk
export const restoreRole = createAsyncThunk(
  "common/restoreEntity",
  async ({ entityType, id }, { rejectWithValue }) => {
    try {
      const data = await commonApi.restoreRole(entityType, id);
      return { entityType, id, data };
    } catch (err) {
      console.log(err);
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        `Failed to restore ${entityType}`;
      return rejectWithValue(errorMessage);
    }
  }
);

// NEW THUNK: Fetch all inventory subcategories
// export const fetchAllInventorySubcategories = createAsyncThunk(
//   "common/fetchAllInventorySubcategories",
//   async (_, { rejectWithValue }) => {
//     try {
//       const data = await commonApi.getAllInventorySubcategories();
//       return data; // array of all subcategories
//     } catch (err) {
//       return rejectWithValue(
//         err.response?.data?.detail || "Failed to load all subcategories"
//       );
//     }
//   }
// );

export const fetchAllInventorySubcategories = createAsyncThunk(
  "common/fetchAllInventorySubcategories",
  async (categoryId, { rejectWithValue }) => {
    try {
      const data = await commonApi.getAllInventorySubcategories(categoryId);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load subcategories"
      );
    }
  }
);

// NEW: Fetch company dropdown
export const fetchCompanyDropdown = createAsyncThunk(
  "common/fetchCompanyDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const data = await commonApi.getCompanyDropdown();
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load company dropdown"
      );
    }
  }
);

// NEW: Fetch vendor dropdown
export const fetchVendorDropdown = createAsyncThunk(
  "common/fetchVendorDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const data = await commonApi.getVendorDropdown();
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load vendor dropdown"
      );
    }
  }
);

// NEW: Fetch department dropdown
export const fetchDepartmentDropdown = createAsyncThunk(
  "common/fetchDepartmentDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const data = await commonApi.getDepartmentDropdown();
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load department dropdown"
      );
    }
  }
);

// NEW: Fetch ticket dropdown
export const fetchTicketDropdown = createAsyncThunk(
  "common/fetchTicketDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const data = await commonApi.getTicketDropdown();
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load ticket dropdown"
      );
    }
  }
);

// NEW: Fetch role dropdown
export const fetchRoleDropdown = createAsyncThunk(
  "common/fetchRoleDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const data = await commonApi.getRoleDropdown();
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load role dropdown"
      );
    }
  }
);

const commonSlice = createSlice({
  name: "common",
  initialState: {
    inventoryCategories: { items: [], loading: false, error: null },
    inventorySubcategories: {
      byCategory: {}, // { "uuid": { items: [], loading: false, error: null } }
    },
    allInventorySubcategories: { items: [], loading: false, error: null },

    companies: { items: [], loading: false, error: null },
    roles: { items: [], loading: false, error: null },

    vendors: { items: [], loading: false, error: null },
    departments: { items: [], loading: false, error: null },

    tickets: { items: [], loading: false, error: null },
    // Existing
    employees: { items: [], loading: false, error: null },
    requests: { items: [], loading: false, error: null },
    subcategories: { items: [], loading: false, error: null },

    // NEW: Issuance Employees
    issuanceEmployeesIT: { items: [], loading: false, error: null },
    issuanceEmployeesNonIT: { items: [], loading: false, error: null },

    // NEW: Ticket Employees
    ticketEmployeesIT: { items: [], loading: false, error: null },
    ticketEmployeesNonIT: { items: [], loading: false, error: null },

    // NEW: Stock (Legacy items + New ByCategory)
    itStock: { items: [], loading: false, error: null },
    nonITStock: { items: [], loading: false, error: null },
    itStockByCategory: { byCategory: {} }, // NEW: { "categoryId": { items: [], loading: false, error: null } }
    nonITStockByCategory: { byCategory: {} }, // NEW

    // NEW: Inventory Dropdown
    inventoryDropdown: { items: [], loading: false, error: null },

    // NEW: Available Inventories
    availableInventories: {
      byRequest: {}, // { "<requestId>": { "<categoryId>": [inventory items...] } }
      loading: false,
      error: null,
    },
  },
  reducers: {
    clearCommonData: (state) => {
      Object.keys(state).forEach((key) => {
        if (state[key]?.items) state[key].items = [];
        if (state[key]?.error) state[key].error = null;
      });
      state.inventorySubcategories.byCategory = {};
      state.itStockByCategory.byCategory = {};
      state.nonITStockByCategory.byCategory = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployeeDropdown.pending, (state) => {
        state.employees.loading = true;
      })
      .addCase(fetchEmployeeDropdown.fulfilled, (state, action) => {
        state.employees.loading = false;
        state.employees.items = action.payload;
      })
      .addCase(fetchEmployeeDropdown.rejected, (state, action) => {
        state.employees.loading = false;
        state.employees.error = action.payload;
      })

      .addCase(fetchRequestDropdown.pending, (state) => {
        state.requests.loading = true;
      })
      .addCase(fetchRequestDropdown.fulfilled, (state, action) => {
        state.requests.loading = false;
        state.requests.items = action.payload;
      })
      .addCase(fetchRequestDropdown.rejected, (state, action) => {
        state.requests.loading = false;
        state.requests.error = action.payload;
      })

      .addCase(fetchCanteenDropdown.pending, (state) => {
        state.subcategories.loading = true;
      })
      .addCase(fetchCanteenDropdown.fulfilled, (state, action) => {
        state.subcategories.loading = false;
        state.subcategories.items = action.payload;
      })
      .addCase(fetchCanteenDropdown.rejected, (state, action) => {
        state.subcategories.loading = false;
        state.subcategories.error = action.payload;
      })

      // NEW: Issuance
      .addCase(fetchIssuanceEmployeesIT.pending, (state) => {
        state.issuanceEmployeesIT.loading = true;
      })
      .addCase(fetchIssuanceEmployeesIT.fulfilled, (state, action) => {
        state.issuanceEmployeesIT.loading = false;
        state.issuanceEmployeesIT.items = action.payload;
      })
      .addCase(fetchIssuanceEmployeesIT.rejected, (state, action) => {
        state.issuanceEmployeesIT.loading = false;
        state.issuanceEmployeesIT.error = action.payload;
      })

      .addCase(fetchIssuanceEmployeesNonIT.pending, (state) => {
        state.issuanceEmployeesNonIT.loading = true;
      })
      .addCase(fetchIssuanceEmployeesNonIT.fulfilled, (state, action) => {
        state.issuanceEmployeesNonIT.loading = false;
        state.issuanceEmployeesNonIT.items = action.payload;
      })
      .addCase(fetchIssuanceEmployeesNonIT.rejected, (state, action) => {
        state.issuanceEmployeesNonIT.loading = false;
        state.issuanceEmployeesNonIT.error = action.payload;
      })

      // NEW: Ticket
      .addCase(fetchTicketEmployeesIT.pending, (state) => {
        state.ticketEmployeesIT.loading = true;
      })
      .addCase(fetchTicketEmployeesIT.fulfilled, (state, action) => {
        state.ticketEmployeesIT.loading = false;
        state.ticketEmployeesIT.items = action.payload;
      })
      .addCase(fetchTicketEmployeesIT.rejected, (state, action) => {
        state.ticketEmployeesIT.loading = false;
        state.ticketEmployeesIT.error = action.payload;
      })

      .addCase(fetchTicketEmployeesNonIT.pending, (state) => {
        state.ticketEmployeesNonIT.loading = true;
      })
      .addCase(fetchTicketEmployeesNonIT.fulfilled, (state, action) => {
        state.ticketEmployeesNonIT.loading = false;
        state.ticketEmployeesNonIT.items = action.payload;
      })
      .addCase(fetchTicketEmployeesNonIT.rejected, (state, action) => {
        state.ticketEmployeesNonIT.loading = false;
        state.ticketEmployeesNonIT.error = action.payload;
      })

      // NEW: Stock (IT) - Handling ByCategory
      .addCase(fetchITStock.pending, (state, action) => {
        const categoryId = action.meta.arg;
        // If a specific category is requested, load into ByCategory
        if (categoryId) {
          if (!state.itStockByCategory.byCategory[categoryId]) {
            state.itStockByCategory.byCategory[categoryId] = { items: [], loading: true, error: null };
          } else {
            state.itStockByCategory.byCategory[categoryId].loading = true;
          }
        } else {
          // Legacy fetch (no category), load into main itStock
          state.itStock.loading = true;
        }
      })
      .addCase(fetchITStock.fulfilled, (state, action) => {
        const { categoryId, data } = action.payload;
        
        if (categoryId) {
          state.itStockByCategory.byCategory[categoryId] = {
            items: data,
            loading: false,
            error: null,
          };
        } else {
          state.itStock.loading = false;
          state.itStock.items = data;
        }
      })
      .addCase(fetchITStock.rejected, (state, action) => {
        const categoryId = action.meta.arg;
        if (categoryId) {
          state.itStockByCategory.byCategory[categoryId] = {
            items: [],
            loading: false,
            error: action.payload,
          };
        } else {
          state.itStock.loading = false;
          state.itStock.error = action.payload;
        }
      })

      // NEW: Stock (Non-IT) - Handling ByCategory
      .addCase(fetchNonITStock.pending, (state, action) => {
        const categoryId = action.meta.arg;
        if (categoryId) {
          if (!state.nonITStockByCategory.byCategory[categoryId]) {
            state.nonITStockByCategory.byCategory[categoryId] = { items: [], loading: true, error: null };
          } else {
            state.nonITStockByCategory.byCategory[categoryId].loading = true;
          }
        } else {
          state.nonITStock.loading = true;
        }
      })
      .addCase(fetchNonITStock.fulfilled, (state, action) => {
        const { categoryId, data } = action.payload;
        if (categoryId) {
          state.nonITStockByCategory.byCategory[categoryId] = {
            items: data,
            loading: false,
            error: null,
          };
        } else {
          state.nonITStock.loading = false;
          state.nonITStock.items = data;
        }
      })
      .addCase(fetchNonITStock.rejected, (state, action) => {
        const categoryId = action.meta.arg;
        if (categoryId) {
          state.nonITStockByCategory.byCategory[categoryId] = {
            items: [],
            loading: false,
            error: action.payload,
          };
        } else {
          state.nonITStock.loading = false;
          state.nonITStock.error = action.payload;
        }
      })

      //c NEW: Inventory Categories
      .addCase(fetchInventoryCategories.pending, (state) => {
        state.inventoryCategories.loading = true;
        state.inventoryCategories.error = null;
      })
      .addCase(fetchInventoryCategories.fulfilled, (state, action) => {
        state.inventoryCategories.loading = false;
        state.inventoryCategories.items = action.payload;
      })
      .addCase(fetchInventoryCategories.rejected, (state, action) => {
        state.inventoryCategories.loading = false;
        state.inventoryCategories.error = action.payload;
      })

      // NEW: Inventory Subcategories
      .addCase(fetchInventorySubcategories.pending, (state, action) => {
        const categoryId = action.meta.arg;
        if (!state.inventorySubcategories.byCategory[categoryId]) {
          state.inventorySubcategories.byCategory[categoryId] = {
            items: [],
            loading: true,
            error: null,
          };
        } else {
          state.inventorySubcategories.byCategory[categoryId].loading = true;
        }
      })
      .addCase(fetchInventorySubcategories.fulfilled, (state, action) => {
        const { categoryId, data } = action.payload;
        state.inventorySubcategories.byCategory[categoryId] = {
          items: data,
          loading: false,
          error: null,
        };
      })
      .addCase(fetchInventorySubcategories.rejected, (state, action) => {
        const categoryId = action.meta.arg;
        state.inventorySubcategories.byCategory[categoryId] = {
          items: [],
          loading: false,
          error: action.payload,
        };
      })

      // NEW: Inventory Dropdown
      .addCase(fetchInventoryDropdown.pending, (state) => {
        state.inventoryDropdown.loading = true;
        state.inventoryDropdown.error = null;
      })
      .addCase(fetchInventoryDropdown.fulfilled, (state, action) => {
        state.inventoryDropdown.loading = false;
        state.inventoryDropdown.items = action.payload;
      })
      .addCase(fetchInventoryDropdown.rejected, (state, action) => {
        state.inventoryDropdown.loading = false;
        state.inventoryDropdown.error = action.payload;
      })
      .addCase(fetchAvailableInventories.pending, (state) => {
        state.availableInventories.loading = true;
        state.availableInventories.error = null;
      })
      .addCase(fetchAvailableInventories.fulfilled, (state, action) => {
        const { requestId, data } = action.payload;
        state.availableInventories.loading = false;
        state.availableInventories.byRequest[requestId] = data;
      })
      .addCase(fetchAvailableInventories.rejected, (state, action) => {
        state.availableInventories.loading = false;
        state.availableInventories.error = action.payload;
      })
      // Restore Role
      .addCase(restoreRole.pending, (state) => {
        // Optional: add a restoring flag if needed across UI
      })
      .addCase(restoreRole.fulfilled, (state, action) => {
        const { entityType, id, data } = action.payload;

        // Optional optimistic update if you cache lists in commonSlice
        // Example for employees:
        // if (entityType === 'employees' && state.employees?.items) {
        //   const index = state.employees.items.findIndex(item => item.id === id);
        //   if (index !== -1) {
        //     state.employees.items[index] = { ...state.employees.items[index], ...data, is_deleted: false };
        //   }
        // }

        // Most reliable: let the component refetch its own list
      })
      .addCase(restoreRole.rejected, (state, action) => {
        // Optional global error handling
      })
      // all subcatgeogires
      .addCase(fetchAllInventorySubcategories.pending, (state) => {
        state.allInventorySubcategories.loading = true;
        state.allInventorySubcategories.error = null;
      })
      .addCase(fetchAllInventorySubcategories.fulfilled, (state, action) => {
        state.allInventorySubcategories.loading = false;
        state.allInventorySubcategories.items = action.payload;
      })
      .addCase(fetchAllInventorySubcategories.rejected, (state, action) => {
        state.allInventorySubcategories.loading = false;
        state.allInventorySubcategories.error = action.payload;
      })
      // companies
      .addCase(fetchCompanyDropdown.pending, (state) => {
        state.companies.loading = true;
        state.companies.error = null;
      })
      .addCase(fetchCompanyDropdown.fulfilled, (state, action) => {
        state.companies.loading = false;
        state.companies.items = action.payload;
      })
      .addCase(fetchCompanyDropdown.rejected, (state, action) => {
        state.companies.loading = false;
        state.companies.error = action.payload;
      })
      // vendor
      .addCase(fetchVendorDropdown.pending, (state) => {
        state.vendors.loading = true;
        state.vendors.error = null;
      })
      .addCase(fetchVendorDropdown.fulfilled, (state, action) => {
        state.vendors.loading = false;
        state.vendors.items = action.payload;
      })
      .addCase(fetchVendorDropdown.rejected, (state, action) => {
        state.vendors.loading = false;
        state.vendors.error = action.payload;
      })
      // department
      .addCase(fetchDepartmentDropdown.pending, (state) => {
        state.departments.loading = true;
        state.departments.error = null;
      })
      .addCase(fetchDepartmentDropdown.fulfilled, (state, action) => {
        state.departments.loading = false;
        state.departments.items = action.payload;
      })
      .addCase(fetchDepartmentDropdown.rejected, (state, action) => {
        state.departments.loading = false;
        state.departments.error = action.payload;
      })
      //ticket
      .addCase(fetchTicketDropdown.pending, (state) => {
        state.tickets.loading = true;
        state.tickets.error = null;
      })
      .addCase(fetchTicketDropdown.fulfilled, (state, action) => {
        state.tickets.loading = false;
        state.tickets.items = action.payload;
      })
      .addCase(fetchTicketDropdown.rejected, (state, action) => {
        state.tickets.loading = false;
        state.tickets.error = action.payload;
      })
      // roles
      .addCase(fetchRoleDropdown.pending, (state) => {
        state.roles.loading = true;
        state.roles.error = null;
      })
      .addCase(fetchRoleDropdown.fulfilled, (state, action) => {
        state.roles.loading = false;
        state.roles.items = action.payload;
      })
      .addCase(fetchRoleDropdown.rejected, (state, action) => {
        state.roles.loading = false;
        state.roles.error = action.payload;
      });
  },
});

export const { clearCommonData } = commonSlice.actions;
export default commonSlice.reducer;
